import React, { useState } from 'react';
import { Star, MapPin, ZoomIn, Plus, X, ImageIcon } from 'lucide-react';

const PortfolioSection = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const filters = [
    { id: 'all', label: 'Všetky projekty', count: 200 },
    { id: 'bratislava', label: 'Bratislava', count: 85 },
    { id: 'kosice', label: 'Košice', count: 45 },
    { id: 'zilina', label: 'Žilina', count: 35 }
  ];

  const projects = [
    {
      id: 1,
      title: 'Moderný byt v Bratislave',
      location: 'Bratislava - Petržalka',
      region: 'bratislava',
      afterImage: '../../assets/images/photos/balkon.webp',
      afterAlt: 'Modern glazed balcony in Bratislava with aluminum sliding panels',
      area: '12 m²',
      duration: '2 dni',
      system: 'Posuvné hliníkové panely',
      glazing: 'Dvojsklo s UV ochranou',
      rating: 5,
      testimonial: 'Perfektná práca, balkón používame celý rok. Hluk z ulice sa znížil o 80%.',
      customer: 'Mária K.',
      completedDate: '2024-09-15',
      price: '780€'
    },
    {
      id: 2,
      title: 'Rodinný dom v Košiciach',
      location: 'Košice - Sever',
      region: 'kosice',
      afterImage: '../../assets/images/photos/main.webp',
      afterAlt: 'Enclosed terrace in Kosice with safety glass and aluminum frames',
      area: '18 m²',
      duration: '3 dni',
      system: 'Skladacie hliníkové panely',
      glazing: 'Trojsklo s tepelnou izoláciou',
      rating: 5,
      testimonial: 'Výborná tepelná izolácia, v zime je na terase príjemne teplo.',
      customer: 'Peter S.',
      completedDate: '2024-08-22',
      price: '1.260€'
    },
    {
      id: 3,
      title: 'Penthouse v Žiline',
      location: 'Žilina - Centrum',
      region: 'zilina',
      afterImage: '../../assets/images/photos/balkon3.webp',
      afterAlt: 'Elegant glazed penthouse balcony in Zilina with panoramic views',
      area: '25 m²',
      duration: '4 dni',
      system: 'Bezrámové zasklenie',
      glazing: 'Laminované bezpečnostné sklo',
      rating: 5,
      testimonial: 'Úžasný výhľad bez rušivých rámov. Kvalita na najvyššej úrovni.',
      customer: 'Ing. Tomáš M.',
      completedDate: '2024-07-10',
      price: '2.100€'
    }
  ];

  const filteredProjects =
    selectedFilter === 'all'
      ? projects
      : projects.filter((project) => project.region === selectedFilter);

  const openLightbox = (project) => {
    setSelectedProject(project);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedProject(null);
  };

  const renderStars = (rating) =>
    Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        size={16}
        className={index < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
      />
    ));

  return (
    <section id="portfolio" className="py-12 sm:py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center space-x-2 bg-orange-50 text-orange-600 px-4 py-2 rounded-full mb-4 sm:mb-6">
            <ImageIcon size={20} />
            <span className="font-semibold text-sm sm:text-base">Naše realizácie</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
            Portfólio projektov
          </h2>
          <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-4">
            Pozrite si ukázky našich realizácií po celom Slovensku. Každý projekt je jedinečný
            a prispôsobený potrebám zákazníka.
          </p>
        </div>

        {/* Filter Tabs - Mobile: Full Width Buttons */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-center gap-3 sm:gap-4 mb-8 sm:mb-12 px-2">
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setSelectedFilter(filter.id)}
              className={`w-full sm:w-auto px-4 sm:px-6 py-3 rounded-full font-semibold transition-all flex items-center justify-between sm:justify-center space-x-2 ${
                selectedFilter === filter.id
                  ? 'bg-orange-400 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-orange-50 border border-gray-200'
              }`}
            >
              <span className="text-sm sm:text-base">{filter.label}</span>
              <span
                className={`text-xs sm:text-sm px-2 py-1 rounded-full ${
                  selectedFilter === filter.id ? 'bg-white/20' : 'bg-gray-100'
                }`}
              >
                {filter.count}
              </span>
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12">
          {filteredProjects.map((project) => {
            const cardImage = project.afterImage || project.beforeImage;
            const cardAlt = project.afterAlt || project.beforeAlt || project.title;

            return (
              <div
                key={project.id}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                onClick={() => openLightbox(project)}
              >
                {/* Single Image */}
                <div className="relative h-48 sm:h-56 overflow-hidden">
                  <img
                    src={cardImage}
                    alt={cardAlt}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                  <div className="absolute top-2 left-2 bg-green-600 text-white px-2 py-1 rounded text-xs font-semibold">
                    Po realizácii
                  </div>
                  <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="text-white text-center">
                      <ZoomIn size={32} className="mx-auto mb-2" />
                      <div className="text-sm font-semibold text-white">Zobraziť detail</div>
                    </div>
                  </div>
                </div>

                {/* Project Info */}
                <div className="p-4 sm:p-6">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                      {project.title}
                    </h3>
                    <div className="text-base sm:text-lg font-bold text-orange-400">{project.price}</div>
                  </div>

                  <div className="flex items-center space-x-2 text-gray-600 mb-3">
                    <MapPin size={16} />
                    <span className="text-xs sm:text-sm">{project.location}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4 text-xs sm:text-sm">
                    <div>
                      <span className="text-gray-600">Plocha:</span>
                      <span className="font-semibold text-gray-900 ml-1">{project.area}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Doba:</span>
                      <span className="font-semibold text-gray-900 ml-1">{project.duration}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1">{renderStars(project.rating)}</div>
                    <div className="text-xs text-gray-500">
                      {new Date(project.completedDate).toLocaleDateString('sk-SK')}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Load More Button */}
        <div className="text-center">
          <button className="inline-flex items-center space-x-2 px-6 sm:px-8 py-3 rounded-full border-2 border-orange-400 text-orange-400 font-semibold hover:bg-orange-400 hover:text-white transition-all">
            <Plus size={20} />
            <span className="text-sm sm:text-base">Zobraziť viac projektov</span>
          </button>
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && selectedProject && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                {selectedProject.title}
              </h3>
              <button
                onClick={closeLightbox}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-6">
              {/* ONE big image */}
              <div className="mb-6 sm:mb-8">
                <img
                  src={selectedProject.afterImage || selectedProject.beforeImage}
                  alt={
                    selectedProject.afterAlt ||
                    selectedProject.beforeAlt ||
                    selectedProject.title
                  }
                  className="w-full max-h-[45vh] object-cover rounded-xl"
                />
              </div>

              {/* Project Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                <div>
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Detaily projektu</h4>
                  <div className="space-y-3 text-sm sm:text-base">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Lokalita:</span>
                      <span className="font-semibold">{selectedProject.location}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Plocha:</span>
                      <span className="font-semibold">{selectedProject.area}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Doba realizácie:</span>
                      <span className="font-semibold">{selectedProject.duration}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Systém:</span>
                      <span className="font-semibold">{selectedProject.system}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Zasklenie:</span>
                      <span className="font-semibold">{selectedProject.glazing}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Celková cena:</span>
                      <span className="font-bold text-orange-400 text-base sm:text-lg">{selectedProject.price}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Hodnotenie zákazníka</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center space-x-1 mb-3">
                      {renderStars(selectedProject.rating)}
                      <span className="ml-2 font-semibold text-sm sm:text-base">{selectedProject.rating}/5</span>
                    </div>
                    <blockquote className="text-gray-600 italic mb-3 text-sm sm:text-base">
                      "{selectedProject.testimonial}"
                    </blockquote>
                    <div className="text-xs sm:text-sm font-semibold text-gray-900">
                      - {selectedProject.customer}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PortfolioSection;